import type { Celda, Indicador, Seccion } from "./modelo"

/**
 * Totales e indicadores para lo que se exporta (29/10). Pedido del usuario: «las
 * totalizaciones y KPIs no están siendo incluidos dentro de estos reportes».
 *
 * ⚠ **Una tabla SIN fila de totales gana uno, calculado de sus columnas de MONTO.** Sólo
 * los montos: una cantidad de días o un número de versión sumados no dicen nada. Y nunca
 * un saldo corrido, una tasa, un precio unitario o un porcentaje, que sumados son un
 * número falso con aspecto de verdadero (`NO_SUMA`).
 *
 * ⚠ **Con una columna «Moneda», un total POR MONEDA.** Sumar dólares con bolívares es
 * justo el error que el resto del sistema evita (los totales van por divisa, nunca
 * convertidos a escondidas).
 */

const NO_SUMA = /saldo|tasa|precio|unit|%|porcentaje|variaci|promedio|cambio|tope|meta|cumplim/i
const MONEDA = /^(moneda|divisa)$/i

export function conTotales(s: Seccion): Seccion {
  if (s.cuadro || (s.pie && s.pie.length > 0)) return s
  const datos = s.filas.filter((f) => !f.grupo)
  if (datos.length < 2) return s
  const sumables = s.columnas.flatMap((c, i) =>
    c.tipo === "monto" && !NO_SUMA.test(c.titulo) ? [i] : [],
  )
  if (sumables.length === 0) return s
  const iMoneda = s.columnas.findIndex((c) => c.tipo === "texto" && MONEDA.test(c.titulo.trim()))
  const grupos = new Map<string, Celda[][]>()
  for (const f of datos) {
    const clave = iMoneda >= 0 ? String(f.celdas[iMoneda] ?? "") : ""
    grupos.set(clave, [...(grupos.get(clave) ?? []), f.celdas])
  }
  // La etiqueta va en la primera columna de texto que no sea la moneda.
  const iEtiqueta = Math.max(
    0,
    s.columnas.findIndex((c, i) => c.tipo === "texto" && i !== iMoneda),
  )
  const pie: Celda[][] = []
  for (const [moneda, filas] of [...grupos.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const fila: Celda[] = s.columnas.map(() => null)
    fila[iEtiqueta] = grupos.size > 1 && moneda ? `Total ${moneda}` : "Total"
    if (iMoneda >= 0) fila[iMoneda] = moneda || null
    for (const i of sumables) {
      const valores = filas.map((c) => c[i]).filter((v): v is number => typeof v === "number")
      fila[i] = valores.length ? Math.round(valores.reduce((a, b) => a + b, 0) * 100) / 100 : null
    }
    pie.push(fila)
  }
  return { ...s, pie }
}

/**
 * Las tarjetas de indicador pintadas dentro de `raiz` (`TarjetaKpi`, `TarjetaNumero` o
 * cualquier elemento con `data-exportar-kpi`), sin repetir la misma dos veces.
 */
export function indicadoresDe(raiz: ParentNode): Indicador[] {
  const salida: Indicador[] = []
  const vistos = new Set<string>()
  for (const el of raiz.querySelectorAll<HTMLElement>("[data-exportar-kpi]")) {
    if (el.closest("[data-exportar='no']")) continue
    const titulo = el.getAttribute("data-kpi-titulo")?.trim() ?? ""
    if (!titulo) continue
    const cantidad =
      el.getAttribute("data-kpi-cantidad") ??
      el.querySelector("[data-kpi-cantidad]")?.textContent?.replace(/\s+/g, " ").trim() ??
      null
    const montos = [...el.querySelectorAll("[data-kpi-monto]")].flatMap((m) => {
      const valor = Number(m.getAttribute("data-kpi-monto"))
      return Number.isFinite(valor)
        ? [{ moneda: m.getAttribute("data-kpi-moneda") ?? "", valor }]
        : []
    })
    const ind: Indicador = {
      titulo,
      detalle: el.getAttribute("data-kpi-detalle"),
      cantidad: cantidad || null,
      montos,
    }
    const clave = JSON.stringify(ind)
    if (vistos.has(clave)) continue
    vistos.add(clave)
    salida.push(ind)
  }
  return salida
}
